/**
 * Date-range picker for activity search (TRAV23 activities-front merge).
 *
 * Named `SimpleDatePicker` upstream; renamed because RSI already has several date
 * pickers. It was a single-day picker at the time of the original merge and became a
 * range picker upstream, so it now writes both startDate and endDate.
 */

import { RootState, AppDispatch } from '@share/utils';
import React, { useState, useRef, useEffect, useMemo } from "react";
import { DateRange, Range, RangeKeyDict } from "react-date-range";
import moment from "moment";
import { FormattedMessage, useIntl } from "react-intl";
import "react-date-range/dist/styles.css";
import "react-date-range/dist/theme/default.css";
import "./style.scss";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCalendarAlt } from "@fortawesome/free-solid-svg-icons";

import { useDispatch, useSelector } from "react-redux";
import { datesActions } from "@share/store/slices";

interface IDatePickerProps {
  onSelect?: () => void;
  isUpdateSearch?: boolean;
  /** Optional whitelist of bookable days (yyyy-MM-dd). When provided, every other day is disabled (Viator window). */
  availableDates?: string[];
}

/**
 * react-date-range takes a colour value, not a CSS variable, so this is resolved once.
 * The activities front passes antd's blue here; keep it, so the calendar reads
 * the same as the live activities app rather than picking up RSI's own accent.
 */
const CALENDAR_ACCENT = "#1890ff";

const toDate = (iso?: string) =>
  iso ? moment(iso, "YYYY-MM-DD").toDate() : new Date();

export const ActivityDatePicker: React.FC<IDatePickerProps> = ({ onSelect, availableDates }) => {
  const dispatch = useDispatch<AppDispatch>();

  const datesStore = useSelector((state: RootState) => state.datesStore);
  const { startDateSelected, endDateSelected, error } = datesStore;

  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const [range, setRange] = useState<Range>(() => ({
    startDate: toDate(startDateSelected),
    endDate: toDate(endDateSelected ?? startDateSelected),
    key: "selection",
  }));

  // react-date-range's Calendar compares the `ranges` prop by REFERENCE in componentDidUpdate;
  // a new array literal each render makes it re-run calcFocusDate and snap the visible month back to
  // range.startDate (today = June). Memoize so the reference only changes on a real selection.
  const ranges = useMemo(() => [range], [range]);

  // Stable lower bound: a fresh `new Date()` per render is also a new prop reference that re-focuses.
  const minDate = useMemo(() => moment().startOf("day").toDate(), []);

  // Sync local range from the store ONLY while the picker is closed (URL restore / external reset).
  // While open the local state is the source of truth; re-syncing here would fight the interaction.
  useEffect(() => {
    if (open) return;
    if (startDateSelected) {
      setRange((prev) => ({
        ...prev,
        startDate: toDate(startDateSelected),
        endDate: toDate(endDateSelected ?? startDateSelected),
      }));
    }
  }, [startDateSelected, endDateSelected, open]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const availableSet = useMemo(
    () => (availableDates && availableDates.length ? new Set(availableDates) : null),
    [availableDates]
  );

  const handleRangeChange = (rangesByKey: RangeKeyDict) => {
    const selection = rangesByKey.selection;
    if (!selection?.startDate || !selection?.endDate) return;

    setRange(selection);
    const startIso = moment(selection.startDate).format("YYYY-MM-DD");
    const endIso = moment(selection.endDate).format("YYYY-MM-DD");

    dispatch(datesActions.setError(""));
    dispatch(datesActions.setDatesSelected({ startDate: startIso, endDate: endIso }));
    onSelect?.();
  };

  const intl = useIntl();

  const formatLabel = () => {
    if (!startDateSelected) return intl.formatMessage({ id: "activity.date.add" });
    const start = moment(range.startDate).format("MMM DD, YYYY");
    const end = range.endDate ? moment(range.endDate).format("MMM DD, YYYY") : start;
    return start === end ? start : `${start} - ${end}`;
  };

  return (
    <div className={`date-picker-container  ${error ? "date-picker-container-error" : ""}`} ref={ref}>
      <div
        className="date-picker-box"
        onClick={() => setOpen((prev) => !prev)}
      >
        <div className="date-picker-leftbox">
          <FontAwesomeIcon
            icon={faCalendarAlt}
            style={{ color: "#27ae60", fontSize: "18px" }}
          />
        </div>

        <div className="date-picker-rightbox">
          <span className="label-title">
            <FormattedMessage id="activity.date.label" />
          </span>
          <span className="label-value">{formatLabel()}</span>
        </div>
      </div>

      {open && (
        <div className="date-picker-dropdown">
          <DateRange
            ranges={ranges}
            onChange={handleRangeChange}
            preventSnapRefocus={true}
            showDateDisplay={false}
            minDate={minDate}
            moveRangeOnFirstSelection={false}
            rangeColors={[CALENDAR_ACCENT]}
            disabledDay={
              availableSet
                ? (date: Date) => !availableSet.has(moment(date).format("YYYY-MM-DD"))
                : undefined
            }
          />
        </div>
      )}
    </div>
  );
};
